import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  TemplateRef,
  ContentChild,
  AfterContentInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';

export interface InfiniteScrollConfig {
  pageSize?: number;
  threshold?: number; // Distance from bottom to trigger load
  loadingTemplate?: TemplateRef<any>;
  emptyStateTemplate?: TemplateRef<any>;
  endOfResultsTemplate?: TemplateRef<any>;
  enableSearch?: boolean;
  enableCategoryFilter?: boolean;
  searchPlaceholder?: string;
  searchDebounceTime?: number;
}

export interface InfiniteScrollQuery {
  page: number;
  pageSize: number;
  search?: string;
  category?: string;
  filters?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface InfiniteScrollResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

@Component({
  selector: 'app-infinite-scroll',
  template: `
    <div class="infinite-scroll-container h-full flex flex-col">
      <!-- Search and Filters -->
      <div
        *ngIf="config.enableSearch || config.enableCategoryFilter || hasFilters"
        class="search-filters-container bg-white/80 backdrop-blur-sm border-b border-white/20 p-4 flex-shrink-0"
      >
        <div class="space-y-4">
          <!-- Search Input -->
          <div *ngIf="config.enableSearch" class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <span class="material-icons text-gray-400 text-sm">search</span>
            </div>
            <input
              #searchInput
              type="text"
              [(ngModel)]="searchTerm"
              (input)="onSearchChange()"
              [placeholder]="config.searchPlaceholder || 'Buscar...'"
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              *ngIf="searchTerm"
              (click)="clearSearch()"
              class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <span class="material-icons text-gray-400 text-sm">close</span>
            </button>
          </div>

          <!-- Category Filter -->
          <div
            *ngIf="config.enableCategoryFilter && categories.length > 0"
            class="flex items-center gap-2 overflow-x-auto"
          >
            <button
              *ngFor="let category of categories; trackBy: trackByCategory"
              (click)="onCategoryChange(category)"
              [class]="getCategoryButtonClass(category)"
              class="flex-shrink-0 px-3 py-2 rounded-full text-sm font-medium transition-all"
            >
              {{ category }}
            </button>
          </div>

          <!-- Custom Filters Slot -->
          <div *ngIf="hasFilters" class="custom-filters">
            <ng-content select="[slot=filters]"></ng-content>
          </div>
        </div>
      </div>

      <!-- Items Container -->
      <div
        #scrollContainer
        class="scroll-container flex-1 overflow-y-auto"
        (scroll)="onScroll($event)"
      >
        <!-- Items Grid/List -->
        <div class="items-container p-4">
          <!-- Loading State (First Load) -->
          <div *ngIf="isLoading && items.length === 0" class="loading-initial">
            <ng-container
              *ngTemplateOutlet="
                config.loadingTemplate || defaultLoadingTemplate
              "
            ></ng-container>
          </div>

          <!-- Empty State -->
          <div *ngIf="!isLoading && items.length === 0" class="empty-state">
            <ng-container
              *ngTemplateOutlet="
                config.emptyStateTemplate || defaultEmptyStateTemplate
              "
            ></ng-container>
          </div>

          <!-- Items -->
          <div *ngIf="items.length > 0" class="items-grid">
            <ng-content></ng-content>
          </div>

          <!-- Loading More Indicator -->
          <div *ngIf="isLoading && items.length > 0" class="loading-more mt-4">
            <div class="flex items-center justify-center py-4">
              <div
                class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"
              ></div>
              <span class="ml-2 text-gray-600">Carregando mais itens...</span>
            </div>
          </div>

          <!-- End of Results -->
          <div *ngIf="!hasMore && items.length > 0" class="end-of-results mt-4">
            <ng-container
              *ngTemplateOutlet="
                config.endOfResultsTemplate || defaultEndTemplate
              "
            ></ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- Default Templates -->
    <ng-template #defaultLoadingTemplate>
      <div class="flex flex-col items-center justify-center py-16">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"
        ></div>
        <p class="text-gray-600">Carregando itens...</p>
      </div>
    </ng-template>

    <ng-template #defaultEmptyStateTemplate>
      <div
        class="flex flex-col items-center justify-center py-16 text-gray-500"
      >
        <div
          class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4"
        >
          <span class="material-icons text-gray-400" style="font-size: 32px;"
            >search_off</span
          >
        </div>
        <h3 class="text-lg font-semibold text-gray-700 mb-2">
          Nenhum item encontrado
        </h3>
        <p class="text-gray-500 text-center">
          Tente ajustar sua busca ou filtros
        </p>
      </div>
    </ng-template>

    <ng-template #defaultEndTemplate>
      <div class="flex items-center justify-center py-4">
        <div class="text-center text-gray-500">
          <span class="material-icons text-gray-400" style="font-size: 24px;"
            >check_circle</span
          >
          <p class="mt-2 text-sm">Todos os itens foram carregados</p>
        </div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .infinite-scroll-container {
        height: 100%;
      }

      .scroll-container {
        scroll-behavior: smooth;
      }

      .scroll-container::-webkit-scrollbar {
        width: 6px;
      }

      .scroll-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
      }

      .scroll-container::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 10px;
      }

      .scroll-container::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class InfiniteScrollComponent<T = any>
  implements OnInit, OnDestroy, AfterContentInit
{
  @Input() config: InfiniteScrollConfig = {};
  @Input() items: T[] = [];
  @Input() categories: string[] = [];
  @Input() hasFilters = false;
  @Input() totalItems = 0;
  @Input() isLoading = false;
  @Input() hasMore = true;

  @Output() loadMore = new EventEmitter<InfiniteScrollQuery>();
  @Output() search = new EventEmitter<string>();
  @Output() categoryChange = new EventEmitter<string>();
  @Output() filtersChange = new EventEmitter<any>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ContentChild('loadingTemplate') loadingTemplate?: TemplateRef<any>;
  @ContentChild('emptyStateTemplate') emptyStateTemplate?: TemplateRef<any>;
  @ContentChild('endOfResultsTemplate') endOfResultsTemplate?: TemplateRef<any>;

  // Internal state
  currentPage = 0;
  searchTerm = '';
  selectedCategory = '';
  private searchSubject = new Subject<string>();
  private subscription = new Subscription();

  // Default configuration
  private defaultConfig: InfiniteScrollConfig = {
    pageSize: 20,
    threshold: 200,
    enableSearch: true,
    enableCategoryFilter: false,
    searchPlaceholder: 'Buscar...',
    searchDebounceTime: 300,
  };

  ngOnInit(): void {
    this.config = { ...this.defaultConfig, ...this.config };
    this.setupSearchDebounce();
    this.loadInitialData();
  }

  ngAfterContentInit(): void {
    // Override default templates with provided ones
    if (this.loadingTemplate) {
      this.config.loadingTemplate = this.loadingTemplate;
    }
    if (this.emptyStateTemplate) {
      this.config.emptyStateTemplate = this.emptyStateTemplate;
    }
    if (this.endOfResultsTemplate) {
      this.config.endOfResultsTemplate = this.endOfResultsTemplate;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchSubject.complete();
  }

  private setupSearchDebounce(): void {
    this.subscription.add(
      this.searchSubject
        .pipe(
          debounceTime(this.config.searchDebounceTime || 300),
          distinctUntilChanged()
        )
        .subscribe((searchTerm) => {
          this.search.emit(searchTerm);
          this.resetPagination();
          this.loadData();
        })
    );
  }

  private loadInitialData(): void {
    this.resetPagination();
    this.loadData();
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  private loadData(): void {
    const query: InfiniteScrollQuery = {
      page: this.currentPage,
      pageSize: this.config.pageSize || 20,
      search: this.searchTerm || undefined,
      category: this.selectedCategory || undefined,
    };

    this.loadMore.emit(query);
    this.currentPage++;
  }

  onScroll(event: Event): void {
    if (this.isLoading || !this.hasMore) {
      return;
    }

    const element = event.target as HTMLElement;
    const threshold = this.config.threshold || 200;

    if (
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold
    ) {
      this.loadData();
    }
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.onSearchChange();
  }

  onCategoryChange(category: string): void {
    this.selectedCategory = category;
    this.categoryChange.emit(category);
    this.resetPagination();
    this.loadData();
  }

  getCategoryButtonClass(category: string): string {
    const baseClass =
      'px-3 py-2 rounded-full text-sm font-medium transition-all';

    if (this.selectedCategory === category) {
      return `${baseClass} bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105`;
    }

    return `${baseClass} bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300`;
  }

  trackByCategory(index: number, category: string): string {
    return category;
  }

  // Scroll to top utility
  scrollToTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }
  }

  // Refresh data
  refresh(): void {
    this.resetPagination();
    this.loadData();
  }
}
